import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Cleave from 'cleave.js/react';
import {useFilter} from './context/filterContext';
import {getSeconds, getTimeString} from '../../utils/date-utils';
import FilterList from '../../components/FilterList';
import FilterRangeControl from './FilterRangeControl';
import CatalogKeyPiano from './CatalogKeyPiano';
import {useCatalog} from './context/catalogContext';

const FilterPanelActions = ({onReset, onApply}) => (
	<div className="catalog__filter-buttons">
		<button
			type="button"
			className="catalog__filter-button catalog__filter-button--reset"
			onClick={(evt) => {
				evt.preventDefault();
				evt.stopPropagation();
				onReset();
			}}
		>
			Сбросить
		</button>
		<button
			type="button"
			className="catalog__filter-button catalog__filter-button--submit"
			onClick={(evt) => {
				evt.preventDefault();
				evt.stopPropagation();
				onApply();
			}}
		>
			Применить
		</button>
	</div>
);

const CatalogFilterChoiceListItem = ({filter, hideActions = false}) => {
	const {showFilteredTracks} = useCatalog();
	const {
		choices, draftChoices, setDraftChoices,
		ranges, length, setLength,
		radios, setRadios,
		setRanges,
		setSearches,
		setFilterHandler, applyFilter, resetFilter,
		setDraftRanges, setDraftLength,
	} = useFilter();

	const [pendingRange, setPendingRange] = useState([0, 0]);
	const [lengthMinInput, setLengthMinInput] = useState('00:00');
	const [lengthMaxInput, setLengthMaxInput] = useState('00:00');

	const getVoiceType = (choice) => {
		let radioName = '';
		switch (choice.toString().toLowerCase()) {
			case 'yes':
				radioName = 'Да';
				break;
			case 'no':
				radioName = 'Нет';
				break;
			default:
				radioName = choice;
		}
		return radioName;
	};

	const pendingChoices = draftChoices[filter.id] ?? choices[filter.id] ?? [];

	const onSliderChange = useCallback((value) => {
		setPendingRange(value);
		if (filter.type === 'length') {
			setLengthMinInput(getTimeString(value[0]));
			setLengthMaxInput(getTimeString(value[1]));
			if (hideActions) {
				setDraftLength({
					min: getTimeString(value[0]),
					max: getTimeString(value[1]),
				});
			}
		}
		if (hideActions && filter.type === 'range') {
			setDraftRanges((prev) => ({...prev, [filter.id]: value}));
		}
	}, [filter.id, filter.type, hideActions, setDraftLength, setDraftRanges]);

	const checkboxHandler = useCallback((evt) => {
		const {checked, value} = evt.target;
		setDraftChoices((prev) => {
			const current = prev[filter.id] ?? choices[filter.id] ?? [];
			const next = checked
				? [...current.filter((choice) => choice !== value), value]
				: current.filter((choice) => choice !== value);
			return {...prev, [filter.id]: next};
		});
	}, [choices, filter.id, setDraftChoices]);

	const toggleKeyChoice = useCallback((value) => {
		setDraftChoices((prev) => {
			const current = prev[filter.id] ?? choices[filter.id] ?? [];
			const next = current.includes(value)
				? current.filter((choice) => choice !== value)
				: [...current, value];
			return {...prev, [filter.id]: next};
		});
	}, [choices, filter.id, setDraftChoices]);

	const closePanel = useCallback(() => {
		setFilterHandler(null)();
	}, [setFilterHandler]);

	const applyChoiceFilter = useCallback(() => {
		applyFilter(filter.id);
		showFilteredTracks();
	}, [applyFilter, filter.id, showFilteredTracks]);

	const applyRadioFilter = useCallback(() => {
		closePanel();
		showFilteredTracks();
	}, [closePanel, showFilteredTracks]);

	const rangeBounds = useMemo(() => {
		if (filter.type === 'range') {
			return {
				min: Number(filter.min) || 0,
				max: Number(filter.max) || 0,
			};
		}
		if (filter.type === 'length') {
			return {
				min: getSeconds(filter.min || '00:00'),
				max: getSeconds(filter.max || '00:00'),
			};
		}
		return {min: 0, max: 0};
	}, [filter]);

	const clampRange = useCallback((next) => {
		let [from, to] = next;
		from = Math.min(Math.max(from, rangeBounds.min), rangeBounds.max);
		to = Math.min(Math.max(to, rangeBounds.min), rangeBounds.max);
		if (from > to) {
			return [to, from];
		}
		return [from, to];
	}, [rangeBounds]);

	const applyRangeFilter = useCallback(() => {
		setRanges((prev) => ({...prev, [filter.id]: pendingRange}));
		setSearches((prev) => [
			...prev.filter((item) => item.type !== filter.id),
			{
				title: `${filter.id.toUpperCase()} ${pendingRange[0]}-${pendingRange[1]}`,
				type: filter.id,
			},
		]);
		closePanel();
		showFilteredTracks();
	}, [closePanel, filter.id, pendingRange, setRanges, setSearches, showFilteredTracks]);

	const resetRangeFilter = useCallback(() => {
		const defaults = [rangeBounds.min, rangeBounds.max];
		setPendingRange(defaults);
		setRanges((prev) => {
			const next = {...prev};
			delete next[filter.id];
			return next;
		});
		setSearches((prev) => prev.filter((item) => item.type !== filter.id));
	}, [filter.id, rangeBounds, setRanges, setSearches]);

	const applyLengthFilter = useCallback(() => {
		const [minSec, maxSec] = pendingRange;
		setLength({
			min: getTimeString(minSec),
			max: getTimeString(maxSec),
		});
		setSearches((prev) => [
			...prev.filter((item) => !String(item.type).startsWith(filter.id)),
			{
				title: `Длина от ${minSec} сек`,
				type: `${filter.id}_min`,
			},
			{
				title: `Длина до ${maxSec} сек`,
				type: `${filter.id}_max`,
			},
		]);
		closePanel();
		showFilteredTracks();
	}, [closePanel, filter.id, pendingRange, setLength, setSearches, showFilteredTracks]);

	const resetLengthFilter = useCallback(() => {
		const defaults = [rangeBounds.min, rangeBounds.max];
		setPendingRange(defaults);
		setLengthMinInput(getTimeString(defaults[0]));
		setLengthMaxInput(getTimeString(defaults[1]));
		setLength({min: '', max: ''});
		setSearches((prev) => prev.filter((item) => !String(item.type).startsWith(filter.id)));
	}, [filter.id, rangeBounds, setLength, setSearches]);

	const lengthInputChange = useCallback((value, type) => {
		if (type === 'min') {
			setLengthMinInput(value);
		} else {
			setLengthMaxInput(value);
		}
		if (value && value.includes(':')) {
			const seconds = getSeconds(value);
			if (!Number.isNaN(seconds)) {
				setPendingRange((prev) => {
					const next = clampRange(
						type === 'min' ? [seconds, prev[1]] : [prev[0], seconds],
					);
					if (hideActions) {
						setDraftLength({
							min: getTimeString(next[0]),
							max: getTimeString(next[1]),
						});
					}
					return next;
				});
			}
		}
	}, [clampRange, hideActions, setDraftLength]);

	const lengthFocusOutHandler = useCallback((value, type) => {
		let time = value;
		if (!time) {
			time = getTimeString(type === 'min' ? rangeBounds.min : rangeBounds.max);
		} else if (value.length === 1) {
			time = `${value}0:00`;
		} else if (value.length === 3) {
			time = `${value}00`;
		} else if (value.length === 4) {
			time = `${value}0`;
		}
		const seconds = getSeconds(time);
		const next = clampRange(
			type === 'min' ? [seconds, pendingRange[1]] : [pendingRange[0], seconds],
		);
		setPendingRange(next);
		setLengthMinInput(getTimeString(next[0]));
		setLengthMaxInput(getTimeString(next[1]));
		if (hideActions) {
			setDraftLength({
				min: getTimeString(next[0]),
				max: getTimeString(next[1]),
			});
		}
	}, [clampRange, hideActions, pendingRange, rangeBounds, setDraftLength]);

	const bpmInputChange = useCallback((evt, type) => {
		const parsed = Number(evt.target.value);
		if (evt.target.value === '' || Number.isNaN(parsed)) {
			return;
		}
		setPendingRange((prev) => {
			const next = clampRange(
				type === 'min' ? [parsed, prev[1]] : [prev[0], parsed],
			);
			if (hideActions) {
				setDraftRanges((current) => ({...current, [filter.id]: next}));
			}
			return next;
		});
	}, [clampRange, filter.id, hideActions, setDraftRanges]);

	const radioHandler = useCallback((evt) => {
		if (evt.target.value === filter.defaultValue) {
			setRadios({...radios, [filter.id]: ''});
			setSearches((prev) => prev.filter((item) => item.type !== filter.id));
		} else {
			setRadios({...radios, [filter.id]: evt.target.value});
			setSearches((prev) => [
				...prev.filter((item) => item.type !== filter.id),
				{
					title: `Вокал: ${getVoiceType(evt.target.value)}`,
					type: filter.id,
				},
			]);
		}
	}, [filter.defaultValue, filter.id, radios, setRadios, setSearches]);

	useEffect(() => {
		if (filter.type === 'range') {
			const current = ranges[filter.id] || filter.defaultValue || [filter.min, filter.max];
			setPendingRange(current.map(Number));
		}
		if (filter.type === 'length') {
			const minSec = getSeconds(length.min || filter.min || '00:00');
			const maxSec = getSeconds(length.max || filter.max || '00:00');
			setPendingRange([minSec, maxSec]);
			setLengthMinInput(getTimeString(minSec));
			setLengthMaxInput(getTimeString(maxSec));
		}
	}, [filter, length.max, length.min, ranges]);

	return (
		<FilterList>
			{
				filter.type === 'checkbox' && filter.id !== 'key'
				&& (
					<li
						className="catalog__filter-settings-item js-catalog-filter--item"
						style={{
							opacity: 1,
							visibility: 'visible',
						}}
					>
						<ul
							className="catalog__filter-settings-item-inner js-smooth-scroll-target"
						>
							{
								filter.choices.map((choice) => (
									<li key={choice}>
										<label
											className="checkbox js-catalog-filter--checkbox d-block"
										>
											<input
												className="checkbox__input"
												type="checkbox"
												onChange={checkboxHandler}
												checked={pendingChoices.includes(choice)}
												value={choice}
											/>
											<span
												className="checkbox__label"
											>
												{choice}
											</span>
										</label>
									</li>
								))
							}
						</ul>
						{!hideActions && (
						<FilterPanelActions
							onReset={() => resetFilter(filter.id)}
							onApply={applyChoiceFilter}
						/>
						)}
					</li>
				)
			}
			{
				filter.id === 'key'
				&& (
					<li
						className="catalog__filter-settings-item catalog__filter-settings-item--key js-catalog-filter--item"
						style={{
							opacity: 1,
							visibility: 'visible',
						}}
					>
						<CatalogKeyPiano
							choices={filter.choices}
							selected={pendingChoices}
							onToggle={toggleKeyChoice}
							onRemove={toggleKeyChoice}
						/>
						{!hideActions && (
						<FilterPanelActions
							onReset={() => resetFilter(filter.id)}
							onApply={applyChoiceFilter}
						/>
						)}
					</li>
				)
			}
			{
				filter.type === 'range'
				&& (
					<li className="catalog__filter-settings-item catalog__filter-settings-item--range js-catalog-filter--item js-catalog-filter--range-parent">
						<FilterRangeControl
							min={rangeBounds.min}
							max={rangeBounds.max}
							value={pendingRange}
							onChange={onSliderChange}
							formatValue={(value) => value}
							label="Темп BPM (Beat Per Minute)"
							fromInput={(
								<input
									className="catalog__filter-timing-field"
									type="number"
									inputMode="numeric"
									value={pendingRange[0]}
									onChange={(evt) => bpmInputChange(evt, 'min')}
									name="catalog-filter-bpm_min"
								/>
							)}
							toInput={(
								<input
									className="catalog__filter-timing-field"
									type="number"
									inputMode="numeric"
									value={pendingRange[1]}
									onChange={(evt) => bpmInputChange(evt, 'max')}
									name="catalog-filter-bpm_max"
								/>
							)}
						/>
						{!hideActions && (
						<FilterPanelActions
							onReset={resetRangeFilter}
							onApply={applyRangeFilter}
						/>
						)}
					</li>
				)
			}
			{
				filter.type === 'length' && length
				&& (
					<li className="catalog__filter-settings-item catalog__filter-settings-item--range js-catalog-filter--item">
						<FilterRangeControl
							min={rangeBounds.min}
							max={rangeBounds.max}
							value={pendingRange}
							onChange={onSliderChange}
							formatValue={getTimeString}
							label="Хронометраж (Min:Sec)"
							fromInput={(
								<Cleave
									placeholder="00:00"
									className="catalog__filter-timing-field js-catalog-filter--length-field js-keyboard-x-arrow-prevent-default"
									options={{
										time: true,
										timePattern: ['m', 's'],
									}}
									value={lengthMinInput}
									name="catalog-filter-length_min"
									onFocus={(e) => e.currentTarget.select()}
									onChange={(evt) => lengthInputChange(evt.target.value, 'min')}
									onBlur={(evt) => lengthFocusOutHandler(evt.target.value, 'min')}
								/>
							)}
							toInput={(
								<Cleave
									placeholder="00:00"
									className="catalog__filter-timing-field js-catalog-filter--length-field js-keyboard-x-arrow-prevent-default"
									options={{
										time: true,
										timePattern: ['m', 's'],
									}}
									value={lengthMaxInput}
									name="catalog-filter-length_max"
									onFocus={(e) => e.currentTarget.select()}
									onChange={(evt) => lengthInputChange(evt.target.value, 'max')}
									onBlur={(evt) => lengthFocusOutHandler(evt.target.value, 'max')}
								/>
							)}
						/>
						{!hideActions && (
						<FilterPanelActions
							onReset={resetLengthFilter}
							onApply={applyLengthFilter}
						/>
						)}
					</li>
				)
			}
			{
				filter.type === 'radio'
				&& (
					<li className="catalog__filter-settings-item js-catalog-filter--item">
						{!hideActions && (
							<p className="text--small title--weight--medium pb-4">{filter.description || filter.title}</p>
						)}
						<ul
							className="catalog__filter-settings-item-inner js-smooth-scroll-target"
						>
							<li className="mb-3">
								<label className="radio js-catalog-filter--radio">
									<input
										className="radio__input"
										type="radio"
										checked={!radios[filter.id]}
										onChange={radioHandler}
										name="catalog-filter-has_vocals"
										value={filter.defaultValue}
									/>
									<span className="radio__label">{filter.defaultValue}</span>
								</label>
							</li>
							{
								filter.choices.map((choice) => (
									<li key={choice} className="mb-3">
										<label className="radio js-catalog-filter--radio">
											<input
												className="radio__input"
												type="radio"
												checked={radios[filter.id] === choice}
												onChange={radioHandler}
												name="catalog-filter-has_vocals"
												value={choice}
											/>
											<span className="radio__label">{getVoiceType(choice)}</span>
										</label>
									</li>
								))
							}
						</ul>
						{!hideActions && (
						<FilterPanelActions
							onReset={() => {
								setRadios((prev) => ({...prev, [filter.id]: ''}));
								setSearches((prev) => prev.filter((item) => item.type !== filter.id));
							}}
							onApply={applyRadioFilter}
						/>
						)}
					</li>
				)
			}
		</FilterList>
	);
};

export default CatalogFilterChoiceListItem;
